import React, { useContext } from 'react';
import { useNavigate } from 'react-router';

import { Button } from 'components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from 'components/ui/dialog';

import toggleFeatureFlagByName from '../../../api/feature-flags/toggleFeatureFlagByName';
import ProjectConfig from '../../../constants';
import { ArcaContext } from '../../../context/ArcaContext.context';

interface AnnounceNewFeatureModalProps {
	featureFlagName: string;
	isOpen: boolean;
	onClose: () => void;
	mainRoleForFeature: 'USER' | 'ADMIN';
	title?: string;
	description?: string;
	imageSrc?: string;
	buttonText?: string;
	path?: string;
	link?: string;
}

const AnnounceNewFeatureModal: React.FC<AnnounceNewFeatureModalProps> = ({
	featureFlagName,
	isOpen,
	onClose,
	mainRoleForFeature,
	title,
	description = '',
	imageSrc = null,
	buttonText = 'OK',
	path = null,
	link = null
}) => {
	const navigate = useNavigate();
	const { token, companyId, userId } = useContext(ArcaContext);

	const computedTitle = title
		? title
		: mainRoleForFeature === 'ADMIN'
			? 'Nueva funcionalidad para administradores'
			: 'Nueva funcionalidad para usuarios';

	const handleButtonClick = async () => {
		// Toggle the feature flag when the button is pressed
		await toggleFeatureFlagByName(token, companyId, userId, featureFlagName);

		if (path) {
			navigate(path, { replace: true });
		} else if (link) {
			window.location.href = link;
		}
		onClose();
	};

	// El Dialog de Radix avisa de apertura Y cierre; `onClose` solo espera el cierre.
	const alCambiar = (abierto: boolean) => {
		if (!abierto) onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={alCambiar}>
			{/* `max-h`/`overflow`: el DialogContent de Radix no los trae, y el Modal de Chakra sí
			    dejaba desplazar. Sin esto, una descripción larga —o el zoom al 200 %— deja el
			    botón y la X fuera de la pantalla, sin manera de alcanzarlos. */}
			<DialogContent className="max-h-[90svh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{computedTitle}</DialogTitle>
				</DialogHeader>

				{imageSrc && (
					<div className="mb-4 text-center">
						{/* `alt=""`: la imagen ilustra la novedad que el texto ya explica. El alt
						    anterior, «Announcement image», estaba en inglés y no decía nada a
						    quien no la ve. */}
						<img src={imageSrc} alt="" className="mx-auto max-h-[200px]" />
					</div>
				)}

				{description && (
					/*
					 * 🔴 La fuente va por `style` y NO con `font-sans`. En chirigota `--font-sans`
					 * es Roboto (index.css), no Inter: su `@theme inline` está más abajo que el de
					 * Proa y gana, como ya avisa el comentario de los imports. Traducir
					 * `fontFamily={INTER}` por `font-sans` habría cambiado la tipografía de este
					 * texto sin que lo pidiera nadie.
					 */
					<p className="text-base font-normal" style={{ fontFamily: ProjectConfig.FONTS.INTER }}>
						{description}
					</p>
				)}

				{buttonText && (
					<DialogFooter>
						<Button onClick={handleButtonClick}>{path ? '¡Quiero verla!' : buttonText}</Button>
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default AnnounceNewFeatureModal;
